import React, { useState } from "react";

import { Button } from "primereact/button";
import { Tag } from "primereact/tag";

import styled from "styled-components";

interface Props {
  pasta?: string | null;
  processo?: string | null;
  origem?: string | null;
  cliente?: string | null;
  autor?: string | null;
  reu?: string | null;
  advogado?: string | null;
  assunto?: string | null;
  acoes?: React.ReactNode;
}

const Quadro = styled.div`
  font-family: var(--font-family);

  .resumo-cabecalho {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;

    h3 {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      font-family: var(--font-family);
      font-size: 1.15rem;
      font-weight: 600;
      color: #495057;

      i {
        color: #2196f3;
      }
    }
  }

  .resumo-corpo {
    background: #f4f6f9;
    border: 1px solid #e3e8ef;
    border-radius: 6px;
  }

  .resumo-faixa {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px 24px;
    padding: 16px 20px;

    & + .resumo-faixa {
      border-top: 1px solid #e3e8ef;
    }
  }

  .resumo-largo {
    grid-column: span 2;
  }

  .resumo-rotulo {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #6c757d;

    i {
      font-size: 12px;
      color: #8a96a8;
    }
  }

  .resumo-valor {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    color: #343a40;
    overflow-wrap: anywhere;
  }

  .resumo-valor-texto {
    font-weight: 500;
  }

  .resumo-vazio {
    color: #adb5bd;
    font-weight: 400;
  }

  .resumo-copiar.p-button {
    width: 26px;
    height: 26px;
    padding: 0;
    color: #6c757d;
  }

  .p-tag {
    font-size: 10px;
    padding: 2px 6px;
  }

  @media (max-width: 960px) {
    .resumo-faixa {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 576px) {
    .resumo-cabecalho {
      flex-direction: column;
      align-items: flex-start;
    }

    .resumo-faixa {
      grid-template-columns: 1fr;
    }

    .resumo-largo {
      grid-column: auto;
    }
  }
`;

const NUMERO_INICIAL = /^\s*([0-9][0-9.\-/]*[0-9])/;

const separarEtiqueta = (pasta: string) => {
  const achado = pasta.match(/^(.*?)\s+(virtual)$/i);
  return achado
    ? { numero: achado[1], etiqueta: achado[2].toUpperCase() }
    : { numero: pasta, etiqueta: null };
};

const BotaoCopiar: React.FC<{ texto: string; rotulo: string }> = ({
  texto,
  rotulo,
}) => {
  const [copiado, setCopiado] = useState(false);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      setCopiado(false);
    }
  };

  return (
    <Button
      type="button"
      icon={copiado ? "pi pi-check" : "pi pi-copy"}
      className="p-button-rounded p-button-text resumo-copiar"
      onClick={copiar}
      tooltip={copiado ? "Copiado" : rotulo}
      tooltipOptions={{ position: "top" }}
      aria-label={rotulo}
    />
  );
};

const Campo: React.FC<{
  icone: string;
  rotulo: string;
  largo?: boolean;
  texto?: boolean;
  vazio: boolean;
}> = ({ icone, rotulo, largo, texto, vazio, children }) => (
  <div className={largo ? "resumo-largo" : undefined}>
    <div className="resumo-rotulo">
      <i className={`pi ${icone}`} />
      {rotulo}
    </div>
    <div className={`resumo-valor${texto ? " resumo-valor-texto" : ""}`}>
      {vazio ? <span className="resumo-vazio">—</span> : children}
    </div>
  </div>
);

Campo.defaultProps = { largo: false, texto: false };

const ResumoDoProcesso: React.FC<Props> = ({
  pasta,
  processo,
  origem,
  cliente,
  autor,
  reu,
  advogado,
  assunto,
  acoes,
}) => {
  const { numero: numeroDaPasta, etiqueta } = separarEtiqueta(pasta ?? "");
  const numeroDoProcesso = processo?.match(NUMERO_INICIAL)?.[1] ?? processo;

  return (
    <Quadro>
      <div className="resumo-cabecalho">
        <h3>
          <i className="pi pi-briefcase" />
          Dados do Processo
        </h3>
        {acoes}
      </div>

      <div className="resumo-corpo">
        <div className="resumo-faixa">
          <Campo icone="pi-folder" rotulo="Pasta" vazio={!numeroDaPasta}>
            {numeroDaPasta}
            {etiqueta ? <Tag value={etiqueta} severity="info" /> : null}
            <BotaoCopiar texto={numeroDaPasta} rotulo="Copiar nº da pasta" />
          </Campo>
          <Campo icone="pi-hashtag" rotulo="Processo nº" vazio={!processo}>
            {processo}
            <BotaoCopiar
              texto={numeroDoProcesso ?? ""}
              rotulo="Copiar nº do processo"
            />
          </Campo>
          <Campo icone="pi-map-marker" rotulo="Origem" vazio={!origem} texto>
            {origem}
          </Campo>
        </div>

        <div className="resumo-faixa">
          <Campo icone="pi-building" rotulo="Cliente" vazio={!cliente}>
            {cliente}
          </Campo>
          <Campo icone="pi-user" rotulo="Autor(es)" vazio={!autor} texto>
            {autor}
          </Campo>
          <Campo icone="pi-users" rotulo="Réu(s)" vazio={!reu} texto>
            {reu}
          </Campo>
        </div>

        <div className="resumo-faixa">
          <Campo
            icone="pi-id-card"
            rotulo="Advogado responsável"
            vazio={!advogado}
          >
            {advogado}
          </Campo>
          <Campo
            icone="pi-align-left"
            rotulo="Assunto"
            vazio={!assunto}
            largo
            texto
          >
            {assunto}
          </Campo>
        </div>
      </div>
    </Quadro>
  );
};

ResumoDoProcesso.defaultProps = {
  pasta: null,
  processo: null,
  origem: null,
  cliente: null,
  autor: null,
  reu: null,
  advogado: null,
  assunto: null,
  acoes: null,
};

export default ResumoDoProcesso;
